import { useEffect, useState } from 'react';
import { useBusiness } from '../context/BusinessContext';
import {
  listAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
} from '../services/api';
import { Appointment, AppointmentStatus } from '../types';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';

const STATUS_OPTIONS: { value: AppointmentStatus; label: string }[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no-show', label: 'No Show' },
];

export default function AppointmentsPage() {
  const { currentBusiness } = useBusiness();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);

  const weekStart = startOfWeek(currentWeek);
  const weekEnd = endOfWeek(currentWeek);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!currentBusiness) return;

      setLoading(true);
      try {
        const data = await listAppointments(
          currentBusiness.id,
          weekStart.toISOString(),
          weekEnd.toISOString()
        );
        setAppointments(data);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [currentBusiness, currentWeek]);

  const handleStatusChange = async (appointmentId: string, newStatus: AppointmentStatus) => {
    if (!currentBusiness) return;

    try {
      await updateAppointment(currentBusiness.id, appointmentId, { status: newStatus });
      setAppointments((prev) =>
        prev.map((apt) =>
          apt.id === appointmentId ? { ...apt, status: newStatus } : apt
        )
      );
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  const handleDelete = async (appointmentId: string) => {
    if (!currentBusiness) return;
    if (!confirm('Are you sure you want to delete this appointment?')) return;

    try {
      await deleteAppointment(currentBusiness.id, appointmentId);
      setAppointments((prev) => prev.filter((apt) => apt.id !== appointmentId));
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  if (!currentBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a business</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-header mb-0">Appointments</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          New Appointment
        </button>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
          className="btn-secondary flex items-center gap-1"
        >
          <ChevronLeftIcon className="h-5 w-5" />
          Previous
        </button>
        <h2 className="text-lg font-medium text-gray-900">
          {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
        </h2>
        <button
          onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
          className="btn-secondary flex items-center gap-1"
        >
          Next
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : appointments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No appointments scheduled for this week
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Customer</th>
                  <th className="table-header">Service</th>
                  <th className="table-header">Date & Time</th>
                  <th className="table-header">Duration</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appointments.map((appointment) => (
                  <tr key={appointment.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <div>
                        <p className="font-medium text-gray-900">
                          {appointment.customerName}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {appointment.customerPhone}
                        </p>
                        {appointment.customerEmail && (
                          <p className="text-gray-500 text-xs">
                            {appointment.customerEmail}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">{appointment.serviceName}</td>
                    <td className="table-cell">
                      {appointment.scheduledAt?.toDate()
                        ? format(
                            appointment.scheduledAt.toDate(),
                            'EEE, MMM d, yyyy h:mm a'
                          )
                        : 'Unknown'}
                    </td>
                    <td className="table-cell">{appointment.duration} min</td>
                    <td className="table-cell">
                      <select
                        value={appointment.status}
                        onChange={(e) =>
                          handleStatusChange(
                            appointment.id,
                            e.target.value as AppointmentStatus
                          )
                        }
                        className={`text-xs font-medium rounded-full px-3 py-1 border-0 focus:ring-2 focus:ring-primary-500 ${
                          appointment.status === 'scheduled'
                            ? 'bg-blue-100 text-blue-800'
                            : appointment.status === 'confirmed'
                            ? 'bg-green-100 text-green-800'
                            : appointment.status === 'completed'
                            ? 'bg-gray-100 text-gray-800'
                            : appointment.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="table-cell">
                      <button
                        onClick={() => handleDelete(appointment.id)}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateAppointmentModal
          businessId={currentBusiness.id}
          services={currentBusiness.services}
          onClose={() => setShowCreateModal(false)}
          onCreated={(newAppointment) => {
            setAppointments((prev) => [...prev, newAppointment]);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}

interface CreateAppointmentModalProps {
  businessId: string;
  services: { id: string; name: string; duration: number }[];
  onClose: () => void;
  onCreated: (appointment: Appointment) => void;
}

function CreateAppointmentModal({
  businessId,
  services,
  onClose,
  onCreated,
}: CreateAppointmentModalProps) {
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    serviceName: services[0]?.name || '',
    serviceId: services[0]?.id || '',
    scheduledAt: '',
    duration: services[0]?.duration || 60,
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const appointment = await createAppointment(businessId, formData);
      onCreated(appointment);
    } catch (error) {
      console.error('Error creating appointment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">New Appointment</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Customer Name *</label>
              <input
                type="text"
                required
                className="input"
                value={formData.customerName}
                onChange={(e) =>
                  setFormData({ ...formData, customerName: e.target.value })
                }
              />
            </div>
            <div>
              <label className="label">Phone *</label>
              <input
                type="tel"
                required
                className="input"
                value={formData.customerPhone}
                onChange={(e) =>
                  setFormData({ ...formData, customerPhone: e.target.value })
                }
              />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={formData.customerEmail}
                onChange={(e) =>
                  setFormData({ ...formData, customerEmail: e.target.value })
                }
              />
            </div>
            <div>
              <label className="label">Service *</label>
              <select
                required
                className="input"
                value={formData.serviceId}
                onChange={(e) => {
                  const service = services.find((s) => s.id === e.target.value);
                  setFormData({
                    ...formData,
                    serviceId: e.target.value,
                    serviceName: service?.name || '',
                    duration: service?.duration || 60,
                  });
                }}
              >
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} ({service.duration} min)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Date & Time *</label>
              <input
                type="datetime-local"
                required
                className="input"
                value={formData.scheduledAt}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledAt: e.target.value })
                }
              />
            </div>
            <div>
              <label className="label">Notes</label>
              <textarea
                className="input"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Creating...' : 'Create Appointment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
